export type SelectedSuggestion = {
  mainText: string;
  secondaryText: string;
};

// Street components as Google returned them, kept in both spellings so the
// mismatch check can accept an abbreviated label. streetNumber is '' when
// the record is route-only (the lot is not held).
export type FoundStreet = {
  streetNumber: string;
  routeLong: string;
  routeShort: string;
};

export type ParsedPlace = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
  formatted: string;
  found: FoundStreet;
  // The suggestion label the user clicked; absent when Google returns no
  // main or secondary text for the prediction.
  selected?: SelectedSuggestion;
};

type AddressComponent = google.maps.places.AddressComponent;
type PlacePrediction = google.maps.places.PlacePrediction;

const findComponent = (
  components: AddressComponent[] | undefined,
  type: string,
): AddressComponent | undefined =>
  components?.find(component => component.types.includes(type));

const longText = (
  components: AddressComponent[] | undefined,
  type: string,
): string => findComponent(components, type)?.longText ?? '';

const shortText = (
  components: AddressComponent[] | undefined,
  type: string,
): string => findComponent(components, type)?.shortText ?? '';

const selectedSuggestion = (
  prediction: PlacePrediction,
): SelectedSuggestion | undefined =>
  prediction.mainText && prediction.secondaryText
    ? {
        mainText: prediction.mainText.text,
        secondaryText: prediction.secondaryText.text,
      }
    : undefined;

export const parsePlaceResult = (
  place: google.maps.places.Place,
  prediction: PlacePrediction,
): ParsedPlace => {
  const components = place.addressComponents ?? undefined;

  const streetNumber = longText(components, 'street_number');
  const route = longText(components, 'route');
  const address1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  const found: FoundStreet = {
    streetNumber,
    routeLong: route,
    routeShort: shortText(components, 'route'),
  };

  const address2 = longText(components, 'subpremise');

  const city =
    longText(components, 'locality') ||
    longText(components, 'sublocality_level_1') ||
    longText(components, 'administrative_area_level_3');

  const state = longText(components, 'administrative_area_level_1');
  const zip = longText(components, 'postal_code');

  // Caller (AddressAutocomplete) guards `place.location` before invoking.
  const location = place.location!;
  const lat = location.lat();
  const lng = location.lng();

  const formatted = place.formattedAddress ?? '';

  return {
    address1,
    address2,
    city,
    state,
    zip,
    lat,
    lng,
    formatted,
    found,
    selected: selectedSuggestion(prediction),
  };
};
